import { UserInputClient } from '@core/api/user-input-client'
import { BaseService } from '@core/base/service/base-service'
import { sleep } from '@core/utils/time'

export type TMarkIssueResolvedForm = {
    action: SelectionOption
    assets: TAsset[]
    resolution_text: string
    technologies: TTechnology[]
}

export const UserInputService =
    new (class UserInputService extends BaseService {
        private readonly client = UserInputClient
        private INTERVAL = 1000 * 5
        private MAX_READINESS_RETRIES = 30

        constructor() {
            super('user.input.service')
        }

        async getFormData(): Promise<
            { actions: SelectionOption[] } | undefined
        > {
            const [error, result] = await this.request(() =>
                this.client.getFormData(this.accountId)
            )

            if (error) {
                return undefined
            }
            return result?.data?.data as {
                actions: SelectionOption[]
            }
        }

        async markIssueAsResolved({
            issueId,
            assets,
            technologies,
            resolution_text,
            action,
        }: { issueId: string } & TMarkIssueResolvedForm): Promise<
            boolean | undefined
        > {
            const assetsAndTech = [
                ...(assets || []),
                ...(technologies || []).map(({ name }) => ({
                    name,
                    type: 'technology',
                })),
            ]
            const [error] = await this.request(() =>
                this.http.post(
                    `/api/accounts/${this.accountId}/issues/${issueId}/resolve`,
                    {
                        resolution_text,
                        action_id: action.id,
                        assets: assetsAndTech,
                    }
                )
            )

            if (error) {
                return undefined
            }
            return true
        }

        async deleteDomainUserInput(id: string): Promise<boolean | undefined> {
            const [error] = await this.request(() =>
                this.client.deleteUserAddedDomain(this.accountId, id)
            )
            if (error) {
                return undefined
            }
            return true
        }

        async pollProcessesUntilReady(resultsCallback: any, delayScan = 0) {
            if (delayScan > 0) {
                await sleep(delayScan)
            }

            return this.request(
                () => this.client.getUserInputsInProgress(this.accountId),
                {
                    poll: {
                        maxRetries: this.MAX_READINESS_RETRIES,
                        delay: this.INTERVAL,
                        resultsCallback,
                        shouldStop: async (response, retriesCompleted) => {
                            const isProcessing =
                                response?.data?.data?.resolved_issues?.length ||
                                response?.data?.data?.delete_domains?.length ||
                                response?.data?.data?.add_domains?.length
                            return !isProcessing
                        },
                    },
                }
            )
        }
    })()
